import * as d3 from 'd3';
import * as $ from 'jquery';
import * as _ from 'lodash';
import * as moment from 'moment';

import { TaskStatus, Group, BatchResult, DateStepResult } from '../../models';
import { DurationFormatter } from './durationFormatter';
import { Tooltip } from './tooltip';
import { DateFormatHelper as dateFormatHelper } from '../../wtFramework';
import getElementSize from '../../common/helpers/getElementSize';
import { taskResultsScale, resultsStatusScale } from '../../common/scales';
import { axisFormat } from './axisFormat';
import { cancelTextColor, createdTextColor, errorTextColor, missingTextColor, startedTextColor, successTextColor, whiteBackgroundColor } from '../../common/constants';

export class TaskProgressChart {
    private _seriesGroup: d3.Selection<{ key: Group, value: BatchResult[] }>;
    private _xScale: d3.time.Scale<number, number>;
    private _xAxis: d3.svg.Axis;
    private _xAxisGroup: d3.Selection<any>;
    private _yScale: d3.scale.Ordinal<string, number>;
    private _yAxis: d3.svg.Axis;
    private _yAxisGroup: d3.Selection<any>;
    private _tooltip: Tooltip;
    private _minHeight = 250;
    private _barHeight = 25;
    private _minWidthForElapsed = 50;
    private _colorScale = taskResultsScale;
    private _textColorScale = d3.scale.ordinal<TaskStatus, string>()
        .domain([
            TaskStatus.Cancelled, TaskStatus.Cancelling, TaskStatus.Completed, TaskStatus.Created,
            TaskStatus.Failed, TaskStatus.Started
        ])
        .range([cancelTextColor, cancelTextColor, successTextColor, missingTextColor, errorTextColor, startedTextColor]);
    private _svg: d3.Selection<any>;

    private _formatHelper: dateFormatHelper;
    private _plotWidth: number;
    private _plotMargins = {
        top: 20,
        bottom: 5,
        left: 150,
        right: 5
    };
    private _plotHeight: number;
    private _batchClick: (d: any, i: any) => void;
    private _labelClick: (d: any, i: any) => void;

    constructor(selector: string) {
        this._formatHelper = new dateFormatHelper();
        let size = getElementSize(selector);
        this.initChart(selector, size.width, size.height);
        ($(selector) as any).resize(() => {
            size = getElementSize(selector);
            this._svg
                .attr('width', size.width);
            const plotWidth = size.width - this._plotMargins.left - this._plotMargins.right;
            this._xScale.range([0, plotWidth]);
            this._xAxisGroup.call(this._xAxis);
            const batches = this._seriesGroup
                .selectAll('.batch');
            batches.selectAll('rect')
                .attr('x', d => this._xScale(new Date(d.range.beginDateTime)))
                .attr('width', d => this._xScale(new Date(d.range.endDateTime))
                    - this._xScale(new Date(d.range.beginDateTime)));
            batches.selectAll('text')
                .classed('hidden', d => (this._xScale(new Date(d.range.endDateTime))
                    - this._xScale(new Date(d.range.beginDateTime))) < this._minWidthForElapsed)
                .attr('x', d => this._xScale(new Date(d.range.beginDateTime))
                    + (this._xScale(new Date(d.range.endDateTime))
                        - this._xScale(new Date(d.range.beginDateTime))) / 2);

        });
    }

    private initChart(selector: string, width: number, height: number) {
        this._svg = d3.select(selector)
            .append('svg')
            .attr('width', width)
            .attr('height', height);

        const plotGroup = this._svg.append('g')
            .classed('plot', true)
            .attr('transform', `translate(${this._plotMargins.left},${this._plotMargins.top})`);

        const plotWidth = width - this._plotMargins.left - this._plotMargins.right;
        this._plotWidth = plotWidth;
        this._xScale = d3.time.scale()
            .range([0, plotWidth]);
        this._xAxis = d3.svg.axis()
            .orient('top')
            .scale(this._xScale);
        this._xAxisGroup = plotGroup.append('g')
            .classed('axis', true);

        this._yScale = d3.scale.ordinal<string, number>();
        this._yAxis = d3.svg.axis()
            .orient('left')
            .scale(this._yScale);
        this._yAxisGroup = plotGroup.append('g')
            .classed('axis', true);
        this._seriesGroup = plotGroup.append('g')
            .classed('series-group', true);
        this._tooltip = new Tooltip(plotGroup)
            .color(d => d.exitCode === TaskStatus.Completed || d.exitCode === TaskStatus.Failed
                ? resultsStatusScale(d.resultsStatus)
                : this._textColorScale(d.exitCode));
    }

    tooltipFormat(): (d: DateStepResult) => string;
    tooltipFormat(value: (d: DateStepResult) => string): this;
    tooltipFormat(value?: (d: DateStepResult) => string): this | ((d: DateStepResult) => string) {
        if (arguments.length) {
            this._tooltip.format(value);
            return this;
        }
        return this._tooltip.format();
    }

    update(data: { key: Group, value: BatchResult[] }[]) {
        const flattened = _.flatten(data.map(d => d.value));
        const minX = d3.min(flattened.map(d => new Date(d.range.beginDateTime)));
        const maxX = d3.max(flattened.map(d => new Date(d.range.endDateTime)));
        const { format, ticks } = axisFormat(minX, maxX, this._plotWidth, this._formatHelper);
        this._xScale.domain([minX, maxX]);
        this._xAxis
            .tickValues(ticks)
            .tickFormat(d3.time.format(format.format));
        this._xAxisGroup.call(this._xAxis);
        this._xAxisGroup.selectAll('.tick')
            .select('text')
            .classed('tickLabel', true);
        const yDomain = data.map(d => d.key.uniqueName);
        const height = yDomain.length * this._barHeight;

        this._plotHeight = height;
        this._yScale.domain(yDomain)
            .rangeBands([0, height], 0.3);
        let svgHeight = height + this._plotMargins.top + this._plotMargins.bottom;
        if (svgHeight < this._minHeight) {
            svgHeight = this._minHeight;
        }
        this._svg.attr('height', svgHeight);
        this._yAxisGroup.call(this._yAxis);
        this._yAxisGroup.selectAll('.tick')
            .select('text')
            .classed('tickLabel', true);

        if (this._labelClick) {
            this._yAxisGroup.selectAll('.tick')
                .classed('link', true)
                .on('click', (d, i) => this._labelClick(_.find(data, dd => dd.key.uniqueName === d), i));
        }

        const self = this;
        const rangeBand = this._yScale.rangeBand();

        const dataBound = this._seriesGroup
            .selectAll('.batch')
            .data(data);
        dataBound
            .exit()
            .remove();
        dataBound
            .enter()
            .append('g')
            .classed('batch', true);
        dataBound.attr('transform', (d) => `translate(${0},${this._yScale(d.key.uniqueName)})`)
            .style('pointer-events', 'all')
            .on('mouseenter', (d) => {
                this._tooltip.move(0, this._yScale(d.key.uniqueName) + rangeBand + 5);
            });
        const rect = dataBound
            .selectAll('.rect')
            .data((d, i) => {
                const values = d.value as (BatchResult & { groupGuid: string })[];
                values.forEach(v => v.groupGuid = d.key.guid);
                return values;
            });
        rect.exit().remove();
        const rectsEnter = rect.enter()
            .append('g')
            .classed('rect', true);
        rectsEnter.append('rect')
            .on('mouseleave', function (d) {
                self._tooltip.hide();
                d3.select(this)
                    .classed('details', false);
            })
            .attr('y', 0)
            .attr('height', rangeBand)
            .attr('x', d => this._xScale(new Date(d.range.beginDateTime)))
            .attr('width', d => this._xScale(new Date(d.range.endDateTime))
                - this._xScale(new Date(d.range.beginDateTime)));
        rectsEnter.append('text')
            .classed('hidden', d => (this._xScale(new Date(d.range.endDateTime))
                - this._xScale(new Date(d.range.beginDateTime))) < this._minWidthForElapsed)
            .attr('x', d => this._xScale(new Date(d.range.beginDateTime))
                + (this._xScale(new Date(d.range.endDateTime))
                    - this._xScale(new Date(d.range.beginDateTime))) / 2)
            .attr('y', 12)
            .style('fill', whiteBackgroundColor)
            .style('text-anchor', 'middle');
        rect.select('rect')
            .on('mouseenter', function (d) {
                const selection = d3.select(this);
                const x = parseFloat(selection.attr('x'));
                const width = parseFloat(selection.attr('width'));
                const tooltipWidth = self._tooltip.width();
                let translateX = x + width / 2 - tooltipWidth / 2;
                if (translateX + tooltipWidth > self._plotWidth) {
                    translateX = self._plotWidth - tooltipWidth;
                } else if (translateX < 0) {
                    translateX = 0;
                }
                let translateY = self._tooltip.translateY();
                if (translateY + self._tooltip.height() > self._plotHeight) {
                    const newTranslateY = translateY - (self._tooltip.height() + self._barHeight);
                    if (newTranslateY > 0) {
                        translateY = newTranslateY;
                    }
                }
                self._tooltip
                    .show()
                    .move(translateX, translateY)
                    .update(d);
                selection.classed('details', true);
            })
            .on('click', function (d, i) {
                self.selectBatch(this, d, i);
            })
            .style('stroke', d => self.strokeRect(d))
            .style('fill', d => this.fillRect(d));
        rect.select('text')
            .text(d => self.sumDurations(d));
    }

    labelClick(value: (d, i) => void): this {
        if (arguments.length) {
            this._labelClick = value;
        }
        return this;
    }

    batchClick(value: (d, i) => void): this {
        if (arguments.length) {
            this._batchClick = value;
        }
        return this;
    }

    selectFirst() {
        const self = this;
        this._seriesGroup
            .selectAll('g.rect')
            .filter((d, i) => i === 0)
            .select('rect')
            .each(function (d, i) {
                self.selectBatch(this, d, i);
            });
    }

    private sumDurations(batchResult: BatchResult): string {
        return DurationFormatter.formatDuration(
            batchResult.stepResults
                .map(sr => moment.duration(sr.elapsed))
                .reduce((prev, cur) => prev.add(cur))
        );
    }

    private selectBatch(node: SVGRectElement, d, i: number) {
        this._seriesGroup
            .selectAll('g.rect')
            .select('rect')
            .classed('selected', false);
        d3.select(node)
            .classed('selected', true);
        if (this._batchClick) {
            this._batchClick(d, i);
        }
    }

    private strokeRect(d: BatchResult): string {
        return d.exitCode === TaskStatus.Created ? cancelTextColor : createdTextColor;
    }

    private fillRect(d: BatchResult): string {
        return d.exitCode === TaskStatus.Completed || d.exitCode === TaskStatus.Failed
            ? resultsStatusScale(d.resultsStatus)
            : this._colorScale(d.exitCode);
    }
}